import {
  MarketListCustomEvent,
  TMarketListSelectionsDidChangeEventDetail,
} from '@market/web-components';
import { ApiExplorerInputType } from '@squareup/dex-types-shared-api-explorer';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import {
  MarketDropdown,
  MarketList,
  MarketPopover,
  MarketRow,
} from '@squareup/dex-ui-shared-market';
import clsx from 'clsx';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ApiExplorerFieldProps } from '../props';

import styles from './api-explorer-field-helpers.module.css';

type SpecialValueDropdownOptionType = '' | null;

interface Props {
  // This must have the slot already set to "trigger"
  trigger: ReactNode;
  showNullable?: boolean | undefined;
  showEmptyString?: boolean | undefined;
  path: string;
  onChange: ApiExplorerFieldProps['onChange'];
  currentValue: ApiExplorerInputType | undefined;
}

interface Option {
  value: string;
  label: string;
  description: string;
  fieldValue: SpecialValueDropdownOptionType;
}

const emptyStringOption: Option = {
  value: 'EMPTY_STRING',
  label: 'Empty string',
  description:
    "A string that contains no characters. In an API request this value will appear as `''`. Can be used to remove a field's value.",
  fieldValue: '',
};

const nullOption: Option = {
  value: 'NULL_VALUE',
  label: 'Null',
  description:
    'A nonexistent value. In an API request this value will appear as `null`. Can be used to clear a field entirely.',
  fieldValue: null,
};

const optionMap = new Map([
  [emptyStringOption.value, emptyStringOption],
  [nullOption.value, nullOption],
]);

const ApiExplorerFieldSpecialValuesDropdown: FC<Props> = ({
  showNullable = false,
  showEmptyString = false,
  trigger,
  path,
  onChange,
  currentValue,
}) => {
  const dropdownRef = useRef<globalThis.HTMLMarketDropdownElement | null>(null);

  const [popoverAdjustment, setPopoverAdjustment] = useState<{
    width: number;
    rightShift: number;
    topShift: number;
  } | null>(null);

  const onPopoverSelectionChange = useCallback(
    ({
      detail,
    }: MarketListCustomEvent<TMarketListSelectionsDidChangeEventDetail>) => {
      const option = optionMap.get(detail.newSelectionValue);
      if (option) {
        onChange(option.fieldValue, path);
      }
    },
    [onChange, path]
  );

  const adjustPopover = useCallback(() => {
    if (!dropdownRef.current) {
      return;
    }
    // MarketDropdown doesn't have a way to provide an alternate reference element
    // for the popover. It will always choose the MarketDropdown as the reference element.
    // To workaround, we'll attempt to correct the popover ourselves. We will:
    // 1. Find our true reference element, which is a parent MarketInputText element.
    // 2. Match our popover's width to be that of the MarketInputText.
    // 3. Adjust the top position of the popover so we appear directly below it.
    // 4. Remove any popover transformation happening

    // The MarketInputText is 2 parent elements above. While not perfect, this will work for now
    const marketInputText = dropdownRef.current?.parentElement?.parentElement;
    if (marketInputText && marketInputText.tagName === 'MARKET-INPUT-TEXT') {
      setPopoverAdjustment({
        width: marketInputText.clientWidth,
        rightShift: marketInputText.clientWidth,
        topShift: marketInputText.clientHeight,
      });
    }

    dropdownRef.current.updateDropdownPosition();
  }, []);

  useEffect(() => {
    adjustPopover();

    // We are relying on `dropdownRef.current` as well as `adjustPopover` to ensure we
    // adjust the popover before we open the dropdown for the first time. Otherwise, you can
    // see a flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownRef.current, adjustPopover]);

  const options = useMemo(() => {
    const results = [];
    if (showEmptyString) {
      results.push(emptyStringOption);
    }

    if (showNullable) {
      results.push(nullOption);
    }

    return results;
  }, [showEmptyString, showNullable]);

  if (options.length === 0) {
    return null;
  }

  const rows = options.map((option) => {
    return (
      <MarketRow key={option.label} size="small" value={option.value}>
        <Paragraph20 weight="medium" as="label" slot="label">
          {option.label}
        </Paragraph20>
        <Paragraph20 as="span" slot="subtext" colorVariant="20">
          <Markdown
            markdown={option.description}
            textColor="currentColor"
            textVariant="20"
          />
        </Paragraph20>
      </MarketRow>
    );
  });

  return (
    <MarketDropdown
      data-testid="api-explorer-field-special-values-dropdown"
      ref={dropdownRef}
      interaction="click"
      className={clsx(styles.dropdown)}
      popoverPlacement="bottom-end"
      onMarketDropdownOpened={adjustPopover}
    >
      <Box slot="trigger" className={styles.trigger}>
        {trigger}
      </Box>
      <MarketPopover
        slot="popover"
        className={clsx(popoverAdjustment && styles['popover-adjust'])}
        style={
          popoverAdjustment
            ? ({
                '--popover-width': `${popoverAdjustment?.width}px`,
                '--popover-top-shift': `${popoverAdjustment?.topShift}px`,
              } as React.CSSProperties)
            : {}
        }
      >
        <MarketList
          onMarketListSelectionsDidChange={onPopoverSelectionChange}
          {...(currentValue !== undefined &&
            currentValue !== null && {
              value: currentValue.toString(),
            })}
        >
          {rows}
        </MarketList>
      </MarketPopover>
    </MarketDropdown>
  );
};

export {
  ApiExplorerFieldSpecialValuesDropdown,
  type SpecialValueDropdownOptionType,
};
